import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Target, Zap } from 'lucide-react';
import Footer from '../components/ui/Footer';
import { Helmet } from 'react-helmet-async';

function About() {
  const { t } = useTranslation();
  const [imageError, setImageError] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(true);

  const values = [
    {
      icon: <Heart className="h-8 w-8 text-blue-600" />,
      title: t('about.values.passion.title'),
      description: t('about.values.passion.description'),
    },
    {
      icon: <Target className="h-8 w-8 text-blue-600" />,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
    },
    {
      icon: <Zap className="h-8 w-8 text-blue-600" />,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('about.metadata.title')}</title>
        <meta name="description" content={t('about.metadata.description')} />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Hero Section - Team Photo */}
        <div
          className="relative w-full"
          style={{ height: '50vh', minHeight: '450px', maxHeight: '600px' }}
        >
          {imageError ? (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
              <div className="p-8 text-center text-white">
                <p className="mb-2 text-xl font-semibold">
                  {t('about.team.photoAlt')}
                </p>
                <p className="text-blue-200">Image non disponible</p>
              </div>
            </div>
          ) : (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex animate-pulse items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
                  <p className="text-gray-600">Chargement...</p>
                </div>
              )}
              <img
                src="/logos/Equipe.png"
                alt={t('about.team.photoAlt')}
                className="h-full w-full object-cover"
                style={{
                  objectPosition: 'center 15%',
                  display: imageLoading ? 'none' : 'block',
                }}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageError(true);
                  setImageLoading(false);
                  console.error('Failed to load team photo: /logos/Equipe.png');
                }}
              />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="mx-auto max-w-7xl text-center">
              <p className="mx-auto max-w-3xl text-lg font-medium text-white drop-shadow-lg md:text-xl">
                {t('about.hero.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Company Info Section */}
        <div className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  {t('about.companyInfo.title')}
                </h2>
                <p className="mb-4 text-lg text-gray-600">
                  {t('about.companyInfo.description')}
                </p>
                <p className="mb-4 text-lg text-gray-600">
                  {t('about.companyInfo.mission')}
                </p>
                <p className="text-lg text-gray-600">
                  {t('about.companyInfo.passion')}
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-8 shadow-lg">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="mb-2 text-4xl font-bold text-blue-600">
                        2023
                      </div>
                      <div className="text-gray-600">
                        {t('about.stats.founded')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="mb-2 text-4xl font-bold text-blue-600">
                        📍
                      </div>
                      <div className="text-gray-600">
                        {t('about.stats.location')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-16 text-center">
              <h2 className="mb-4 text-3xl font-bold text-gray-900">
                {t('about.values.title')}
              </h2>
              <p className="mx-auto max-w-3xl text-xl text-gray-600">
                {t('about.values.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-gradient-to-br from-blue-50 to-white p-8 shadow-lg transition-shadow hover:shadow-xl"
                >
                  <div className="mb-4 flex justify-center">{value.icon}</div>
                  <h3 className="mb-3 text-center text-xl font-semibold text-gray-900">
                    {value.title}
                  </h3>
                  <p className="text-center text-gray-600">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mb-16 bg-gradient-to-br from-blue-600 to-blue-800 py-24 text-white">
          <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="mb-6 text-3xl font-bold">{t('about.cta.title')}</h2>
            <p className="mx-auto mb-8 max-w-2xl text-xl text-blue-100">
              {t('about.cta.description')}
            </p>
            <a
              href="https://calendar.app.google/Hmv3ohtGtQmrtfaL8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg bg-white px-8 py-3 font-semibold text-blue-600 shadow-lg transition-colors hover:bg-blue-50"
            >
              {t('about.cta.button')}
            </a>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default About;
